import { AddPhotoAlternate, Close } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "../StructuredPromptImageCanvas.module.scss";
import { ResizeHandle } from "../types";
import { ResizeHandles } from "./ResizeHandles";

interface EmbedAreaBoxProps {
	/** Pixel rect on the canvas (already converted from the stored % bbox). */
	bbox: { left: number; top: number; width: number; height: number };
	/** The image to embed, or null while the area is still empty. */
	file: File | null;
	onFileChange: (file: File | null) => void;
	onMouseDown: (e: React.MouseEvent, handle: ResizeHandle) => void;
	onRemove: () => void;
}

/**
 * The committed free-drawn embed area.
 *
 * The picked image previews INSIDE the rect at reduced opacity rather than in a floating
 * panel: the area is a statement about a place on the image, so the preview belongs in
 * that place, and translucency keeps the scene underneath readable while you position it.
 * That also removes the only reason this component needed a popover.
 */
export const EmbedAreaBox = ({ bbox, file, onFileChange, onMouseDown, onRemove }: EmbedAreaBoxProps) => {
	const { t } = useTranslation("fibo", { keyPrefix: "lab.canvas" });
	const { left, top, width, height } = bbox;
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	useEffect(() => {
		if (!file) {
			setPreviewUrl(null);
			return;
		}
		const url = URL.createObjectURL(file);
		setPreviewUrl(url);
		return () => URL.revokeObjectURL(url);
	}, [file]);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const picked = e.target.files?.[0] ?? null;
		e.target.value = ""; // allow re-picking the same file
		if (picked) onFileChange(picked);
	};

	// Only the "add image" chip opens the picker. Making the whole rect clickable would
	// fight the drag-to-move gesture that shares the same surface.
	const rectStyle = { left: `${left}px`, top: `${top}px`, width: `${width}px`, height: `${height}px` };

	return (
		<Box data-embed-area>
			<input
				ref={fileInputRef}
				type="file"
				accept="image/*"
				style={{ display: "none" }}
				onChange={handleFileChange}
			/>

			<Box className={styles.embedAreaRect} style={rectStyle} onMouseDown={(e) => onMouseDown(e, null)}>
				{previewUrl && <img src={previewUrl} alt={t("embedAreaLabel")} className={styles.embedAreaPreview} />}

				{!file && (
					<Box
						className={styles.embedAreaAddChip}
						onMouseDown={(e) => e.stopPropagation()}
						onClick={(e) => {
							e.stopPropagation();
							fileInputRef.current?.click();
						}}
					>
						<AddPhotoAlternate className={styles.embedAreaAddIcon} />
						<Typography className={styles.embedAreaAddText}>{t("embedAddImage")}</Typography>
					</Box>
				)}
			</Box>

			<Typography className={styles.embedAreaLabel} style={{ left: `${left}px`, top: `${top}px` }}>
				{t("embedAreaLabel")}
			</Typography>

			<Box
				className={styles.embedAreaRemoveBtn}
				style={{ left: `${left + width}px`, top: `${top}px` }}
				onMouseDown={(e) => e.stopPropagation()}
				onClick={(e) => {
					e.stopPropagation();
					onRemove();
				}}
			>
				<Close sx={{ fontSize: 10, color: "white" }} />
			</Box>

			<ResizeHandles bbox={bbox} onMouseDown={onMouseDown} />
		</Box>
	);
};
